import { CarouselJsonLd } from 'next-seo'
import { Router, useRouter } from 'next/router'

import clsx from 'clsx'

import { getBaseUrl } from '@/lib/contentful/utils'

import { ALGOLIA_APPS_HITS_PER_PAGE } from '@/utils/apps/getAppParams'
import { isNonNullable } from '@/utils/isNonNullable'

import { FullScreenLoading } from '@/components/FullScreenLoading'

import { Pagination } from '../../Pagination'
import { AppFeedCard } from '../AppFeedCard/AppFeedCard'
import { useSearchStatus } from '../AppsSearch/AppsSearch'
import { useAppFeed } from './useAppFeed'

export function getAppParentCategoryFromPath(asPath: Router['asPath']) {
	return (
		asPath.split('?')?.at(0)?.split('/categories/')?.[1]?.split('/')?.filter(Boolean)?.at(0) ?? ''
	)
}

type Props = {
	className?: string
}

export function AppFeed({ className }: Props) {
	const { locale } = useRouter()

	const { status, results } = useAppFeed()
	const hits = results?.hits

	const { isTyping } = useSearchStatus()

	const isLoading = status === 'stalled' || status === 'loading' || isTyping || !Array.isArray(hits)

	// Create AppFeed grid based on loading status and hits results
	let appFeedResults
	if (!isLoading && Array.isArray(hits) && hits.length === 0) {
		appFeedResults = (
			<p className="italic text-lg">
				There are no exact matches for your search criteria at this time.&nbsp;
				<span className="font-semibold">Below are some of our most popular integrations.</span>
			</p>
		)
	} else if (!isLoading && Array.isArray(hits)) {
		appFeedResults = (
			<>
				<CarouselJsonLd
					ofType="default"
					data={hits?.map((app) => ({
						url: `${getBaseUrl(locale)}/apps/${app.slug}/`
					}))}
				/>
				<ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
					{hits?.map((app) => {
						return (
							<li key={app.slug}>
								<AppFeedCard
									logo={app.logo}
									name={app.title}
									slug={app.slug}
									monthlyPrice={app.monthlyPrice}
									upfrontPrice={app.upfrontPrice}
									reviewCount={app.reviewCount}
									pricingType={app.pricingType}
									rating={app.rating}
									featured={app.featured}
									summary={app.summary}
									tier={app.tier}
									categories={
										app.appCategories
											? Array.from(
													new Map(
														Object.values(app.appCategories ?? {}).map((parentCategory) => {
															if (!parentCategory) return [null, null]

															const propertyName = Object.keys(parentCategory)[0]

															const name = (
																parentCategory[propertyName as keyof typeof parentCategory] as {
																	title: string
																}
															)?.title
															const slug = (
																parentCategory[propertyName as keyof typeof parentCategory] as {
																	slug: string
																}
															)?.slug

															return [
																slug,
																{
																	slug,
																	name
																}
															]
														})
													).values()
											  ).filter(isNonNullable)
											: []
									}
								/>
							</li>
						)
					})}
				</ol>

				<div className="inline-flex pt-16">
					<Pagination limit={ALGOLIA_APPS_HITS_PER_PAGE} total={results?.nbHits ?? 0} />
				</div>
			</>
		)
	} else {
		appFeedResults = null
	}

	return (
		<section className={clsx('w-full', className)}>
			<div className="container relative">
				{<FullScreenLoading show={isLoading} />}
				<div className={`transition-opacity${isLoading ? ' opacity-50' : ''}`}>
					{appFeedResults}
				</div>
			</div>
		</section>
	)
}
